import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CreditCard, Calendar, CheckCircle, XCircle, Clock, AlertTriangle, Settings, Users, Sparkles, Zap, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { format, differenceInDays } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import AdminSubscriptionManager from './AdminSubscriptionManager';
import { useTranslation } from 'react-i18next';
import stripeService, { PricePlan } from '@/services/StripeService';

const SubscriptionSettings = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const { toast } = useToast();
  const { 
    currentUser, 
    userData, 
    userRole,
    subscriptionData, 
    refreshSubscription 
  } = useAuth();

  const currentLocale = i18n.language === 'es' ? es : enUS;
  const plans = stripeService.getPlans();
  const trialDays = stripeService.getTrialDays();
  const stripeConfigured = stripeService.isConfigured();

  const checkSubscription = useCallback(async () => {
    try {
      setLoading(true);
      await refreshSubscription();
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [refreshSubscription]);

  const handleSubscribe = async (plan: PricePlan) => {
    if (!currentUser || !currentUser.email) {
      toast({
        title: t('common.error'),
        description: t('settings.subscription.auth_error'),
        variant: 'destructive',
      });
      return;
    }

    if (!stripeConfigured) {
      toast({
        title: t('settings.subscription.title'),
        description: t('settings.subscription.stripe_not_configured', 
          'Stripe no está configurado aún. Contacte al administrador para activar los cobros.'),
      });
      return;
    }

    try {
      setCheckoutLoading(plan.id);
      await stripeService.createCheckoutSession(
        currentUser.uid,
        currentUser.email,
        plan.stripe_price_id,
      );
    } catch (error: unknown) {
      const err = error as Error;
      console.error('Checkout error:', err);
      toast({
        title: t('common.error'),
        description: err.message || 'Error al procesar el pago',
        variant: 'destructive',
      });
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!currentUser) return;

    if (!stripeConfigured) {
      toast({
        title: t('settings.subscription.title'),
        description: t('settings.subscription.stripe_not_configured', 
          'Stripe no está configurado aún. Contacte al administrador.'),
      });
      return;
    }

    try {
      setPortalLoading(true);
      await stripeService.openCustomerPortal(currentUser.uid);
    } catch (error: unknown) {
      const err = error as Error;
      console.error('Portal error:', err);
      toast({
        title: t('common.error'),
        description: err.message || 'Error al abrir el portal',
        variant: 'destructive',
      });
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      checkSubscription();
    }
  }, [currentUser, checkSubscription]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'trialing':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'grace':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'past_due':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'canceled':
      case 'inactive':
      default:
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">{t('settings.subscription.active')}</Badge>;
      case 'trialing':
        return <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">{t('settings.subscription.trialing', 'Periodo de Prueba')}</Badge>;
      case 'grace':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">{t('settings.subscription.grace')}</Badge>;
      case 'past_due':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">{t('settings.subscription.pending')}</Badge>;
      case 'canceled':
        return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">{t('settings.subscription.canceled', 'Cancelada')}</Badge>;
      case 'inactive':
      default:
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">{t('settings.subscription.inactive')}</Badge>;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('settings.subscription.active_desc');
      case 'trialing':
        return t('settings.subscription.trialing_desc', `Estás disfrutando tu periodo de prueba gratuito de ${trialDays} días. Al finalizar, se activará tu suscripción automáticamente.`);
      case 'grace':
        return t('settings.subscription.grace_desc');
      case 'past_due':
        return t('settings.subscription.pending_desc');
      case 'canceled':
        return t('settings.subscription.canceled_desc', 'Tu suscripción ha sido cancelada. Puedes reactivarla en cualquier momento.');
      case 'inactive':
      default:
        return t('settings.subscription.inactive_desc');
    }
  };

  // ─── Render: Not Authenticated ────────────────────────────
  if (!currentUser) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" />
            {t('settings.subscription.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-gray-600">{t('settings.subscription.auth_error')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Render: Loading ──────────────────────────────────────
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" />
            {t('settings.subscription.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">{t('settings.subscription.refreshing')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Check if user has active subscription ────────────────
  const isSubscribed = subscriptionData?.subscribed && 
    ['active', 'trialing', 'grace', 'past_due'].includes(subscriptionData?.status || '');

  // ─── Trial info ───────────────────────────────────────────
  const trialEndDate = subscriptionData?.trial_end ? new Date(subscriptionData.trial_end) : null;
  const trialDaysRemaining = trialEndDate ? differenceInDays(trialEndDate, new Date()) : 0;

  return (
    <div className="space-y-6">
      {/* ═══ Current Status Card ═══ */}
      {subscriptionData && subscriptionData.status !== 'inactive' ? (
        <Card className="border-purple-200 dark:border-purple-800/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <CreditCard className="mr-2 h-5 w-5 text-purple-600" />
                {t('settings.subscription.title')}
              </div>
              {getStatusBadge(subscriptionData.status)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Overview */}
            <div className="flex items-center space-x-3 p-4 bg-muted/50 rounded-lg">
              {getStatusIcon(subscriptionData.status)}
              <div className="flex-1">
                <h3 className="font-semibold">{subscriptionData.subscription_tier || 'Pro'}</h3>
                <p className="text-sm text-muted-foreground">
                  {getStatusText(subscriptionData.status)}
                </p>
              </div>
            </div>

            {/* Trial Banner */}
            {subscriptionData.status === 'trialing' && trialEndDate && (
              <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <span className="font-semibold text-purple-800 dark:text-purple-300">
                    {t('settings.subscription.trial_banner', 'Periodo de prueba gratuito')}
                  </span>
                </div>
                <p className="text-sm text-purple-700 dark:text-purple-400">
                  {t('settings.subscription.trial_remaining', {
                    days: trialDaysRemaining,
                    defaultValue: `Te quedan ${trialDaysRemaining} días de prueba gratuita. Tu suscripción se activará el ${format(trialEndDate, 'dd/MM/yyyy', { locale: currentLocale })}.`
                  })}
                </p>
              </div>
            )}

            {/* Subscription Dates */}
            {subscriptionData.current_period_end && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Calendar className="mr-2 h-4 w-4" />
                <span>
                  {subscriptionData.cancel_at_period_end 
                    ? `${t('settings.subscription.expires', 'Vence')}: ` 
                    : `${t('settings.subscription.renewal', 'Renovación')}: `}
                  {format(new Date(subscriptionData.current_period_end), 'dd/MM/yyyy', { locale: currentLocale })}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button 
                onClick={handleManageSubscription}
                disabled={portalLoading}
                variant="outline"
                className="flex-1"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <Settings className="mr-2 h-4 w-4" />
                    {t('settings.subscription.manage_btn')}
                  </>
                )}
              </Button>

              <Button 
                onClick={checkSubscription}
                variant="ghost"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  t('settings.subscription.refresh')
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* ═══ No Active Subscription: Show Plans ═══ */
        <>
          <Card>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl flex items-center justify-center gap-2">
                <Zap className="h-6 w-6 text-purple-600" />
                {t('settings.subscription.choose_plan', 'Elige tu Plan')}
              </CardTitle>
              <CardDescription>
                {t('settings.subscription.trial_info', `Todos los planes incluyen ${trialDays} días de prueba gratuita. Sin cargos durante el periodo de prueba.`)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Billing Interval Toggle */}
              <div className="flex justify-center mb-6">
                <div className="bg-muted rounded-full p-1 flex gap-1">
                  <button
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      billingInterval === 'month' 
                        ? 'bg-background shadow text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setBillingInterval('month')}
                  >
                    {t('settings.subscription.monthly', 'Mensual')}
                  </button>
                  <button
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      billingInterval === 'year' 
                        ? 'bg-background shadow text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setBillingInterval('year')}
                  >
                    {t('settings.subscription.yearly', 'Anual')}
                    <Badge className="ml-2 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">
                      -20%
                    </Badge>
                  </button>
                </div>
              </div>

              {/* Plan Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {plans
                  .filter(p => p.interval === billingInterval)
                  .map((plan) => (
                    <Card 
                      key={plan.id} 
                      className={`relative overflow-hidden transition-shadow hover:shadow-lg ${
                        plan.popular 
                          ? 'border-purple-500 dark:border-purple-400 shadow-md' 
                          : 'border-border'
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute top-0 right-0 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                          {t('settings.subscription.popular', 'Popular')}
                        </div>
                      )}
                      <CardContent className="p-6 space-y-4">
                        <div>
                          <h3 className="text-xl font-bold">{plan.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                        </div>

                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-extrabold">${plan.price.toFixed(2)}</span>
                          <span className="text-muted-foreground">
                            /{plan.interval === 'month' 
                              ? t('settings.subscription.per_month', 'mes') 
                              : t('settings.subscription.per_year', 'año')}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {plan.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>

                        <Button
                          onClick={() => handleSubscribe(plan)}
                          disabled={checkoutLoading === plan.id}
                          className={`w-full ${
                            plan.popular 
                              ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                              : ''
                          }`}
                        >
                          {checkoutLoading === plan.id ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {t('settings.subscription.processing')}
                            </>
                          ) : (
                            <>
                              <CreditCard className="mr-2 h-4 w-4" />
                              {t('settings.subscription.start_trial', `Iniciar ${trialDays} días gratis`)}
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                          <Shield className="h-3 w-3" />
                          {t('settings.subscription.secure', 'Pago seguro vía Stripe')}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
              </div>

              {/* No Stripe Configured Warning */}
              {!stripeConfigured && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg text-center">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    <AlertTriangle className="inline h-4 w-4 mr-1" />
                    {t('settings.subscription.stripe_pending', 'Los cobros se activarán próximamente. Configure las llaves de Stripe en las variables de entorno.')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Admin Panel */}
      {userRole === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="mr-2 h-5 w-5" />
              {t('settings.subscription.admin_panel')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AdminSubscriptionManager />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionSettings;
